import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Send, Sparkles, User, RotateCcw, ArrowRight, AlertTriangle } from 'lucide-react';
import { gaEvent } from '../lib/analytics';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import { API } from '../api';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';

const SUGGESTIONS = [
  'Which hospitals near McAllen Transitional Care Center have the strongest referral opportunity?',
  'Which physicians overlap between nearby hospitals and Burns Nursing Home?',
  'What is the best strategy to increase short-term census at Burns Nursing Home?',
  'Which home health agencies are connected to the market around Burns Nursing Home?',
  'Which nearby facilities compete for the same referral sources as Burns Nursing Home?',
  'Who owns Burnam, Soon\'s nursing homes?',
];

export default function AskAIPage() {
  const [params] = useSearchParams();
  const initialQ = params.get('q') || '';
  const [messages, setMessages] = useState([
    { id: 'msg-0', role: 'assistant', text: "Hi — I'm your Healthcare Strategy AI. I can analyze referral patterns, physician alignment, hospital networks, managed-care opportunity, post-acute partners, and competitive positioning for any SNF or hospital in our index. Try one of the suggestions or ask anything strategic." },
  ]);
  const [input, setInput] = useState(initialQ);
  const [thinking, setThinking] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);
  const sentInitial = useRef(false);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, thinking]);

  useEffect(() => {
    if (initialQ && !sentInitial.current) {
      sentInitial.current = true;
      handleSend(initialQ);
    }
    // eslint-disable-next-line
  }, []);

  const handleSend = async (text) => {
    const q = (text ?? input).trim();
    if (!q || thinking) return;
    setMessages((m) => [...m, { id: `u-${Date.now()}`, role: 'user', text: q }]);
    setInput('');
    setThinking(true);
    gaEvent('ai_question_sent', { question_length: q.length });
    try {
      const r = await axios.post(`${API}/ask`, { question: q, session_id: sessionId }, { timeout: 120000, withCredentials: true });
      const d = r.data;
      if (d.session_id && !sessionId) setSessionId(d.session_id);
      setMessages((m) => [...m, { id: `a-${Date.now()}`, role: 'assistant', text: d.answer, sources: d.sources || [], contextCount: d.context_count }]);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Unknown error';
      setMessages((m) => [...m, { id: `e-${Date.now()}`, role: 'assistant', text: `The assistant hit an error: ${msg}`, error: true }]);
    } finally {
      setThinking(false);
    }
  };

  const reset = () => {
    setMessages([messages[0]]);
    setSessionId(null);
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-[920px] mx-auto px-6 lg:px-8 pt-6 pb-10">
        <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'Ask AI' }]} />

        <div className="mt-6">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-[#4A7C5E]/30 bg-white">
            <Sparkles size={12} className="text-[#4A7C5E]"/>
            <span className="text-[11px] font-semibold uppercase tracking-wider text-[#4A7C5E]">Beta · Claude Sonnet 4.5</span>
          </div>
          <h1 className="mt-4 font-display text-4xl font-semibold text-[#1A2622] tracking-tight">Healthcare Strategy AI</h1>
          <p className="mt-3 text-[14px] text-[#3A4A44] max-w-2xl">Connected across CMS facilities, hospitals, physicians, home health, hospice, managed-care, census, and NPPES. Ask about referrals, market growth, physician targeting, post-acute partners, and competitive positioning.</p>
        </div>

        {/* Chat window */}
        <div className="mt-7 rounded-xl bg-white border border-[#E5DCC4] overflow-hidden flex flex-col" style={{ height: 560 }}>
          <div ref={scrollRef} className="flex-1 px-5 md:px-6 py-5 space-y-4 overflow-y-auto">
            {messages.map((m, i) => (
              <div key={m.id || `m-${i}`} className={`flex gap-3 ${m.role === 'user' ? 'justify-end' : ''}`}>
                {m.role === 'assistant' && (
                  <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${m.error ? 'bg-[#C75D5D]' : 'bg-[#1A2622]'} text-[#FBF6EC]`}>
                    {m.error ? <AlertTriangle size={14}/> : <Sparkles size={14}/>}
                  </div>
                )}
                <div className={`max-w-[82%] rounded-xl px-4 py-3 ${m.role === 'user' ? 'bg-[#1A2622] text-[#FBF6EC]' : 'bg-[#F5EFDD] text-[#1A2622]'}`}>
                  {m.role === 'assistant' ? (
                    <div className="ai-md text-[14px] leading-relaxed" data-testid="msg-assistant"><ReactMarkdown>{m.text}</ReactMarkdown></div>
                  ) : (
                    <p className="text-[14px] leading-relaxed whitespace-pre-wrap" data-testid={`msg-${m.role}`}>{m.text}</p>
                  )}
                  {m.sources && m.sources.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {m.sources.map((s, j) => (
                        <Link key={j} to={s.to} data-testid="ai-source-chip" className="inline-flex items-center gap-1 text-[11.5px] font-medium px-2.5 py-1 rounded-md bg-white border border-[#E5DCC4] text-[#2D5F4F] hover:bg-[#FBF6EC]">{s.label}</Link>
                      ))}
                    </div>
                  )}
                  {m.contextCount && (
                    <div className="mt-2 text-[10px] text-[#7A857F]" data-testid="msg-context-counts">
                      Grounded on {[
                        ['facilities', 'facility', 'facilities'], ['hospitals', 'hospital', 'hospitals'],
                        ['physicians', 'physician', 'physicians'], ['owners', 'owner', 'owners'],
                      ].filter(([k]) => m.contextCount[k]).map(([k, one, many]) => `${m.contextCount[k]} ${m.contextCount[k] === 1 ? one : many}`).join(' · ') || 'platform data'}
                    </div>
                  )}
                </div>
                {m.role === 'user' && (
                  <div className="w-8 h-8 rounded-md bg-[#F5EFDD] text-[#1A2622] flex items-center justify-center shrink-0"><User size={14}/></div>
                )}
              </div>
            ))}
            {thinking && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-md bg-[#1A2622] text-[#FBF6EC] flex items-center justify-center"><Sparkles size={14}/></div>
                <div className="px-4 py-3 rounded-xl bg-[#F5EFDD] text-[#1A2622] inline-flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#4A7C5E] animate-bounce" style={{ animationDelay: '0ms' }}/>
                  <span className="w-1.5 h-1.5 rounded-full bg-[#4A7C5E] animate-bounce" style={{ animationDelay: '120ms' }}/>
                  <span className="w-1.5 h-1.5 rounded-full bg-[#4A7C5E] animate-bounce" style={{ animationDelay: '240ms' }}/>
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-[#F0E7D2] px-3 md:px-4 py-3 bg-[#FBF6EC]/40">
            <div className="flex items-center gap-2">
              <input data-testid="ai-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }} placeholder="Ask about referrals, hospitals, physicians, managed care, post-acute network…" className="flex-1 h-11 px-4 rounded-md bg-white border border-[#E5DCC4] text-[13.5px] placeholder:text-[#A8B0AA] outline-none focus:border-[#4A7C5E]"/>
              <button onClick={reset} className="h-11 w-11 rounded-md bg-white border border-[#E5DCC4] flex items-center justify-center text-[#7A857F] hover:text-[#1A2622] hover:border-[#4A7C5E]" title="Reset chat"><RotateCcw size={14}/></button>
              <button onClick={() => handleSend()} disabled={thinking} className="h-11 px-4 rounded-md bg-[#1A2622] text-[#FBF6EC] inline-flex items-center gap-1.5 text-[13px] font-medium hover:bg-[#2D5F4F] disabled:opacity-50" data-testid="ai-send"><Send size={13}/> Send</button>
            </div>
          </div>
        </div>

        {messages.length <= 1 && (
          <div className="mt-6">
            <div className="text-[11px] uppercase tracking-wider text-[#7A857F] mb-3">Try one of these</div>
            <div className="flex flex-wrap gap-2">
              {SUGGESTIONS.map((s) => (
                <button key={s} onClick={() => handleSend(s)} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md bg-white border border-[#E5DCC4] text-[12.5px] text-[#3A4A44] hover:border-[#4A7C5E] hover:text-[#1A2622]">
                  {s} <ArrowRight size={11}/>
                </button>
              ))}
            </div>
          </div>
        )}

        <p className="mt-7 text-[11px] text-[#A8B0AA]">Answers may not be perfect — always verify against the underlying CMS data linked in sources.</p>
      </main>
      <Footer />
    </div>
  );
}
